import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { NaturalLanguageInput } from './QueryInput/NaturalLanguageInput';
import { QueryBuilder } from './QueryInput/QueryBuilder';
import { QueryConfirmation } from './QueryConfirmation';
import { ResultsCard } from './ResultsCard/ResultsCard';
import { PatientListView } from './PatientList/PatientListView';
import { SuggestedQueries } from './SuggestedQueries';
import { SaveToDashboardModal } from './SaveToDashboard/SaveToDashboardModal';
import { useAnalyticsStore } from '../stores/analyticsStore';
import { useFilterCatalog } from '../hooks/useAnalyticsQuery';
import type { ListResult } from '../types/analytics.types';

export function AnalyticsPage() {
  const [saveToDashboardOpen, setSaveToDashboardOpen] = useState(false);

  const {
    mode,
    setMode,
    uiState,
    currentQuery,
    queryResult,
    selectedVisualization,
    error,
    reset,
  } = useAnalyticsStore();
  useFilterCatalog();

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const handleSwitchMode = () => {
    setMode(mode === 'natural_language' ? 'builder' : 'natural_language');
  };

  const renderContent = () => {
    if (uiState === 'translating') {
      return (
        <Card className="p-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Understanding your question...</p>
          </div>
        </Card>
      );
    }

    if (uiState === 'loading') {
      return (
        <Card className="p-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Finding patients...</p>
          </div>
        </Card>
      );
    }

    if (uiState === 'confirming' && currentQuery) {
      return <QueryConfirmation query={currentQuery} />;
    }

    if (uiState === 'results' && queryResult && selectedVisualization) {
      return (
        <ResultsCard
          result={queryResult}
          visualization={selectedVisualization}
          onSaveToDashboard={() => setSaveToDashboardOpen(true)}
        />
      );
    }

    if (uiState === 'patient_list' && queryResult && queryResult.data.type === 'list') {
      return <PatientListView data={queryResult.data as ListResult} />;
    }

    return (
      <div className="space-y-6">
        <Card className="p-4 md:p-6">
          <Tabs value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
            <TabsList className="mb-4">
              <TabsTrigger value="natural_language">Natural Language</TabsTrigger>
              <TabsTrigger value="builder">Query Builder</TabsTrigger>
            </TabsList>

            <TabsContent value="natural_language">
              <NaturalLanguageInput onSwitchMode={handleSwitchMode} />
            </TabsContent>

            <TabsContent value="builder">
              <QueryBuilder onSwitchMode={handleSwitchMode} />
            </TabsContent>
          </Tabs>
        </Card>

        <SuggestedQueries />
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
        <p className="text-muted-foreground">
          Ask questions about your patients in plain language
        </p>
      </div>

      {error && uiState === 'idle' && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {renderContent()}

      {currentQuery && selectedVisualization && (
        <SaveToDashboardModal
          open={saveToDashboardOpen}
          onClose={() => setSaveToDashboardOpen(false)}
          query={currentQuery}
          visualizationType={selectedVisualization}
        />
      )}
    </div>
  );
}
